import { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABEL, STATUS_LABEL } from "@/lib/constants";
import { Users, UserCheck, UserX, ShieldAlert, FileWarning, Layers, CheckCircle2, Printer, Upload, Clock, FileText, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { AssignedAreasBadges } from "@/components/AssignedAreas";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilePreview } from "@/components/FilePreview";
import { toast } from "sonner";

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [progress, setProgress] = useState(null);
  const [tpsInfo, setTpsInfo] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    if (user?.role === "ppdp") {
      api.get("/ppdp/progress").then(({ data }) => setProgress(data)).catch(() => {});
      if (user.assigned_tps) {
        api.get(`/tps/${user.assigned_tps}/wilayah`).then(({ data }) => setTpsInfo(data)).catch(() => {});
      }
    }
  }, [user?.role, user?.assigned_tps, refreshKey]);

  const reload = () => setRefreshKey((k) => k + 1);

  const isAreaBound = user?.role === "ppdp" || user?.role === "rt" || user?.role === "rw";
  // PPDP TPS-based → sembunyikan card yang redundant (info sudah ada di card Wilayah Kerja)
  const isPpdpTps = user?.role === "ppdp" && !!user?.assigned_tps;
  const ppdpAreas = user?.assigned_areas?.length
    ? user.assigned_areas
    : (user?.assigned_dusuns || []).map((d) => ({ dusun: d }));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Ringkasan</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">
          Selamat datang, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-slate-600 mt-2">
          Anda masuk sebagai <span className="font-semibold">{ROLE_LABEL[user?.role] || user?.role}</span>.
        </p>
        {user?.role === "ppdp" && user.assigned_tps && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold mb-1.5">Area Tugas</div>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-3 py-1 text-xs font-semibold">
              {user.assigned_tps}
            </span>
          </div>
        )}
        {user?.role === "ppdp" && !user.assigned_tps && ppdpAreas.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold mb-1.5">Area Tugas</div>
            <AssignedAreasBadges areas={ppdpAreas} />
          </div>
        )}
      </header>

      {/* TPS wilayah kerja info card (PPDP) */}
      {user?.role === "ppdp" && tpsInfo && (
        <section
          className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 md:p-6"
          data-testid="ppdp-tps-info-card"
        >
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-800 font-bold">Wilayah Kerja</div>
              <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-1">
                {tpsInfo.kode}
                {tpsInfo.nama && tpsInfo.nama !== tpsInfo.kode && (
                  <span className="ml-2 text-slate-500 font-normal text-lg">· {tpsInfo.nama}</span>
                )}
              </h2>
              <div className="text-sm text-slate-700 mt-1">
                {tpsInfo.dusun_label || tpsInfo.dusuns?.join(" & ")}
              </div>
              {tpsInfo.lokasi && (
                <div className="text-xs text-slate-500 mt-1">Lokasi: {tpsInfo.lokasi}</div>
              )}
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total Pemilih</div>
              <div className="text-3xl font-bold tabular-nums text-slate-900" data-testid="ppdp-total-pemilih">
                {tpsInfo.total_pemilih.toLocaleString("id-ID")}
              </div>
              <div className="flex gap-1.5 justify-end mt-1 text-[11px] font-semibold">
                <span className="rounded bg-blue-100 text-blue-800 px-1.5 py-0.5">L {tpsInfo.laki_laki.toLocaleString("id-ID")}</span>
                <span className="rounded bg-pink-100 text-pink-800 px-1.5 py-0.5">P {tpsInfo.perempuan.toLocaleString("id-ID")}</span>
                {tpsInfo.total_kk != null && (
                  <span className="rounded bg-amber-100 text-amber-800 px-1.5 py-0.5" data-testid="ppdp-total-kk">KK {tpsInfo.total_kk.toLocaleString("id-ID")}</span>
                )}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Rincian RW / RT</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {tpsInfo.rw_breakdown?.map((rw) => (
                <div
                  key={rw.rw}
                  className="rounded-md bg-white border border-slate-200 px-3 py-2 text-sm flex items-center justify-between"
                  data-testid={`ppdp-rw-${rw.rw}`}
                >
                  <div>
                    <span className="font-semibold">RW {rw.rw}</span>
                    <span className="text-slate-500 text-xs ml-1">
                      RT {rw.rts.map((rt) => rt.rt).join(", ")}
                    </span>
                  </div>
                  <span className="tabular-nums text-slate-700 font-semibold">{rw.subtotal.toLocaleString("id-ID")}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PPDP-specific progress section */}
      {user?.role === "ppdp" && progress?.areas && (
        <section className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Progress Coklit per Area</div>
            <p className="text-sm text-slate-600 mt-1">
              Tandai pemilih yang sudah dicek di menu <Link to="/app/coklit" className="font-semibold underline">Coklit</Link>. Setelah 100% dan tidak ada pengajuan pending, Anda bisa cetak &amp; submit Berita Acara.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {progress.areas.map((a) => (
              <PpdpAreaCard key={a.label} area={a} onChanged={reload} />
            ))}
          </div>
        </section>
      )}

      {/* Bento grid — untuk PPDP TPS-based, skip tile "Total Pemilih" besar (redundant dgn Wilayah Kerja) */}
      <section className={`grid grid-cols-1 gap-4 ${isPpdpTps ? "md:grid-cols-3" : "md:grid-cols-4"}`}>
        {!isPpdpTps && (
          <StatTile
            label="Total Pemilih"
            value={stats?.total}
            icon={Users}
            tone="primary"
            testid="stat-total"
            className="md:col-span-2 md:row-span-2"
            large
          />
        )}
        <StatTile label="Aktif" value={stats?.by_status?.ACTIVE} icon={UserCheck} tone="success" testid="stat-active" />
        <StatTile label="Perlu Verifikasi" value={stats?.by_status?.PERLU_VERIFIKASI} icon={ShieldAlert} tone="warning" testid="stat-perlu" />
        <StatTile label="Meninggal" value={stats?.by_status?.MENINGGAL} icon={UserX} tone="muted" testid="stat-meninggal" />
        <StatTile label="Pindah" value={stats?.by_status?.PINDAH_DOMISILI} icon={UserX} tone="muted" testid="stat-pindah" />
        <StatTile label="Data Ganda" value={stats?.by_status?.DATA_GANDA} icon={FileWarning} tone="danger" testid="stat-ganda" />
        <StatTile label="Pengajuan Pending" value={stats?.pending_changes} icon={Layers} tone="primary" testid="stat-pending" />
      </section>

      {/* Gender + Area — sembunyikan untuk PPDP TPS-based (redundant dgn card Wilayah Kerja) */}
      {!isPpdpTps && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-md border border-slate-200 bg-white p-6">
            <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold mb-4">Jenis Kelamin</div>
            <div className="grid grid-cols-2 gap-4">
              <BigStat label="Laki-laki" value={stats?.by_gender?.L} accent="bg-slate-900" />
              <BigStat label="Perempuan" value={stats?.by_gender?.P} accent="bg-red-600" />
            </div>
          </div>
          <div className="rounded-md border border-slate-200 bg-white p-6">
            <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold mb-4">
              {isAreaBound ? "Per Area Tugas" : "Per Dusun"}
            </div>
            <AreaBreakdown stats={stats} isPPDP={isAreaBound} />
          </div>
        </section>
      )}
      {/* Quick links */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <QuickLink to="/app/voters" label="Data Pemilih" />
        <QuickLink to="/app/coklit" label="Coklit" />
        {(user?.role === "admin" || user?.role === "panitia") && (
          <>
            <QuickLink to="/app/verifikasi" label="Verifikasi Pengajuan" />
            <QuickLink to="/app/audit" label="Audit Log" />
          </>
        )}
      </section>
    </div>
  );
}

function AreaBreakdown({ stats, isPPDP }) {
  const total = stats?.total || 1;
  const items = isPPDP
    ? (stats?.by_area || []).map((a) => ({ key: a.label, label: a.label, count: a.count }))
    : (stats?.by_dusun || []).map((d) => ({ key: d.nama, label: d.nama, count: d.count }));
  if (items.length === 0) {
    return <div className="text-sm text-slate-500">Belum ada data.</div>;
  }
  return (
    <div className="space-y-2">
      {items.map((it) => {
        const pct = total ? Math.round((it.count / total) * 100) : 0;
        return (
          <div key={it.key}>
            <div className="flex justify-between text-sm">
              <span className="font-semibold">{it.label}</span>
              <span className="text-slate-600 font-mono">{it.count} · {pct}%</span>
            </div>
            <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-slate-900" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatTile({ label, value, icon: Icon, tone = "primary", className = "", large = false, testid }) {
  const tones = {
    primary: "bg-slate-900 text-white",
    success: "bg-emerald-50 text-emerald-900 border-emerald-200",
    warning: "bg-amber-50 text-amber-900 border-amber-200",
    danger: "bg-red-50 text-red-900 border-red-200",
    muted: "bg-slate-50 text-slate-700 border-slate-200",
  };
  const isPrimary = tone === "primary";
  return (
    <div
      data-testid={testid}
      className={`rounded-md p-5 ${isPrimary ? tones.primary : `bg-white border ${tones[tone]}`} ${className}`}
    >
      <div className="flex items-start justify-between">
        <div className={`text-xs uppercase tracking-[0.2em] font-semibold ${isPrimary ? "text-slate-300" : "text-slate-500"}`}>
          {label}
        </div>
        <Icon className={`h-4 w-4 ${isPrimary ? "text-slate-300" : "opacity-60"}`} />
      </div>
      <div className={`font-display ${large ? "text-5xl md:text-6xl" : "text-3xl"} font-bold tracking-tight mt-3`}>
        {(value ?? 0).toLocaleString("id-ID")}
      </div>
    </div>
  );
}

function BigStat({ label, value, accent }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className={`inline-block h-3 w-3 rounded-full ${accent}`} />
        <span className="font-display text-3xl font-bold tracking-tight">{(value ?? 0).toLocaleString("id-ID")}</span>
      </div>
    </div>
  );
}

function QuickLink({ to, label }) {
  return (
    <Link to={to} className="rounded-md border border-slate-200 bg-white px-4 py-3 text-sm font-semibold hover:bg-slate-50 transition-colors">
      {label} →
    </Link>
  );
}

const BA_STATUS_LABEL = {
  PENDING: "Menunggu Verifikasi Panitia",
  APPROVED: "Disetujui — Pekerjaan Selesai",
  REJECTED: "Ditolak — Perlu Diajukan Ulang",
};

const BA_STATUS_CLASS = {
  PENDING: "bg-amber-50 border-amber-200 text-amber-900",
  APPROVED: "bg-emerald-50 border-emerald-200 text-emerald-900",
  REJECTED: "bg-red-50 border-red-200 text-red-900",
};

function PpdpAreaCard({ area, onChanged }) {
  const [openSubmit, setOpenSubmit] = useState(false);
  const done = area.ba_status === "APPROVED";
  const pending = area.ba_status === "PENDING";
  const rejected = area.ba_status === "REJECTED";
  const pct = area.percent_active ?? area.percent ?? 0;
  const pctOverall = area.percent ?? 0;
  // Legacy dashboard: dusun-based; New: TPS-based (assigned_tps)
  const isTpsArea = !!area.tps;

  const downloadBa = async () => {
    try {
      const params = isTpsArea
        ? { tps: area.tps, doc_type: "DPS" }
        : { dusun: area.dusun, rw: area.rw || undefined, rt: area.rt || undefined, doc_type: "DPS" };
      const res = await api.get("/reports/berita-acara", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.target = "_blank";
      link.download = `BA_${(area.tps || area.dusun || "area").replace(/\s/g, "_")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadExcelCoklit = async () => {
    try {
      const params = {};
      if (isTpsArea && area.tps) params.tps = area.tps;
      if (area.dusun) params.dusun = area.dusun;
      if (area.rw) params.rw = area.rw;
      if (area.rt) params.rt = area.rt;
      const res = await api.get("/exports/voters-all", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      link.download = m ? m[1] : `Data_Pemilih_${(area.tps || area.dusun || "area").replace(/\s/g, "_")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      toast.success("Excel hasil coklit diunduh.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <article
      className={`rounded-md border bg-white p-5 ${done ? "border-emerald-300 ring-1 ring-emerald-200" : "border-slate-200"}`}
      data-testid={`ppdp-area-${area.tps || area.dusun}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display font-bold text-lg">{area.label}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {area.total_registered ?? area.total} pemilih terdaftar
            {area.total_kk != null && <> · <b className="text-slate-700">{area.total_kk.toLocaleString("id-ID")} KK</b></>}
          </div>
        </div>
        {done && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-3 py-1 text-xs font-bold">
            <CheckCircle2 className="h-3.5 w-3.5" /> SELESAI
          </span>
        )}
      </div>

      {/* Progress Coklit — hasil kerja PPDP */}
      <div className="mt-4 grid grid-cols-2 gap-2" data-testid={`ppdp-progress-mini-${area.tps || area.dusun}`}>
        <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-800">Sudah Dicoklit</div>
          <div className="text-2xl font-bold tabular-nums text-emerald-900" data-testid="stat-sudah">{(area.sudah_coklit ?? 0).toLocaleString("id-ID")}</div>
        </div>
        <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-700">Belum Dicoklit</div>
          <div className="text-2xl font-bold tabular-nums text-slate-900" data-testid="stat-belum">{(area.belum_coklit ?? 0).toLocaleString("id-ID")}</div>
        </div>
        <div className="rounded-md bg-red-50 border border-red-200 p-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-red-800">TMS (Dikeluarkan)</div>
          <div className="text-2xl font-bold tabular-nums text-red-900" data-testid="stat-tms">{(area.tms_count ?? 0).toLocaleString("id-ID")}</div>
        </div>
        <div className="rounded-md bg-blue-50 border border-blue-200 p-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-blue-800">Tambahan Baru</div>
          <div className="text-2xl font-bold tabular-nums text-blue-900" data-testid="stat-tambahan">{(area.tambahan_baru ?? 0).toLocaleString("id-ID")}</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="flex justify-between text-sm">
          <span className="font-semibold">Progress Keseluruhan</span>
          <span className="font-mono text-slate-700" data-testid="progress-text">{area.sudah_coklit ?? 0}/{area.total_registered ?? 0} · {pctOverall}%</span>
        </div>
        <div className="mt-1.5 h-3 rounded-full bg-slate-100 overflow-hidden">
          <div
            className={`h-full transition-all ${pctOverall === 100 ? "bg-emerald-600" : "bg-slate-900"}`}
            style={{ width: `${pctOverall}%` }}
          />
        </div>
        {area.changes_pending > 0 && (
          <div className="mt-2 text-xs text-amber-700 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {area.changes_pending} pengajuan menunggu verifikasi Panitia.
          </div>
        )}
      </div>

      {/* BA status block */}
      {area.ba_status && (
        <div className={`mt-4 rounded-md border p-3 text-sm ${BA_STATUS_CLASS[area.ba_status]}`}>
          <div className="font-bold flex items-center gap-2">
            {done && <CheckCircle2 className="h-4 w-4" />}
            {BA_STATUS_LABEL[area.ba_status]}
          </div>
          {area.ba_submitted_at && (
            <div className="text-xs opacity-80 mt-0.5">
              Diajukan: {new Date(area.ba_submitted_at).toLocaleString("id-ID")}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {!done && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={downloadBa}
            className="font-semibold"
            data-testid={`ppdp-print-ba-${area.tps || area.dusun}`}
          >
            <Printer className="h-4 w-4 mr-1" /> Cetak BA
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadExcelCoklit}
            className="font-semibold"
            data-testid={`ppdp-download-excel-${area.tps || area.dusun}`}
          >
            <Download className="h-4 w-4 mr-1" /> Unduh Excel
          </Button>
          <Button
            size="sm"
            onClick={() => setOpenSubmit(true)}
            disabled={!area.can_submit_ba}
            className="bg-slate-900 hover:bg-slate-800 font-semibold"
            data-testid={`ppdp-submit-ba-${area.tps || area.dusun}`}
          >
            <Upload className="h-4 w-4 mr-1" /> {pending ? "Menunggu" : rejected ? "Ajukan Ulang" : "Submit BA"}
          </Button>
        </div>
      )}
      {pct < 100 && !done && (
        <p className="text-[11px] text-slate-500 mt-2">
          Selesaikan coklit semua {area.total} pemilih aktif untuk dapat mencetak &amp; submit Berita Acara.
        </p>
      )}

      {openSubmit && (
        <BaSubmitDialog
          area={area}
          onClose={() => setOpenSubmit(false)}
          onDone={() => { setOpenSubmit(false); onChanged?.(); }}
        />
      )}
    </article>
  );
}

function BaSubmitDialog({ area, onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("DPS");
  const [catatan, setCatatan] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFiles((f) => [...f, data]);
      toast.success("Foto terunggah");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async () => {
    if (files.length === 0) { toast.error("Minimal 1 foto BA tertanda tangan"); return; }
    setSubmitting(true);
    try {
      await api.post("/ba-submissions", {
        dusun: area.dusun || null,
        rw: area.rw || null,
        rt: area.rt || null,
        tps: area.tps || null,
        doc_type: docType,
        signed_file_ids: files.map((f) => f.id),
        catatan: catatan || null,
      });
      toast.success("BA berhasil diajukan ke Panitia.");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Submit Berita Acara</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm">
            <div className="font-semibold">Area: {area.label}</div>
            <div className="text-xs text-slate-600">{area.total} pemilih · {area.verified}/{area.total} terverifikasi</div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Jenis Dokumen</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="h-11" data-testid="ba-doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="DPS">BA Penetapan DPS</SelectItem>
                <SelectItem value="DPSHP">BA DPS Hasil Perbaikan</SelectItem>
                <SelectItem value="DPT">BA Penetapan DPT</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Foto BA Tertanda Tangan *</Label>
            <p className="text-[11px] text-slate-500">Unggah foto BA yang sudah ditandatangani oleh PPDP, Ketua RT, dan Ketua RW.</p>
            <label className="flex items-center justify-center gap-2 h-12 rounded-md border-2 border-dashed border-slate-300 bg-slate-50 text-slate-700 font-semibold cursor-pointer hover:bg-slate-100">
              <Upload className="h-4 w-4" />
              {uploading ? "Mengunggah..." : "Pilih / Foto BA"}
              <input type="file" className="hidden" accept="image/*,application/pdf" capture="environment" onChange={onUpload} data-testid="ba-upload-input" />
            </label>
            {files.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {files.map((f) => (
                  <div key={f.id} className="relative">
                    <FilePreview fileId={f.id} compact />
                    <button
                      type="button"
                      onClick={() => setFiles((arr) => arr.filter((x) => x.id !== f.id))}
                      className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-600 text-white text-xs"
                      aria-label="Hapus"
                    >×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Catatan (opsional)</Label>
            <Input value={catatan} onChange={(e) => setCatatan(e.target.value)} className="h-11" placeholder="cth: BA ditandatangani 1 Juni 2026 di Posyandu" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={submitting || files.length === 0} className="bg-slate-900 hover:bg-slate-800" data-testid="ba-submit-button">
            {submitting ? "Mengirim..." : "Kirim ke Panitia"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
